import { useState, useEffect } from 'react';
import { apiGet } from '@/lib/api';
import { DollarSign, TrendingUp, Briefcase, Activity, Target, FileText, ChevronRight, ArrowUpRight, ArrowDownRight, AlertTriangle, Info, Search } from 'lucide-react';
import '@/styles/authenticated.css';
import { formatDate, formatInAppZone } from '@/lib/formatters';


const fmt = (v) => v ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
const fmtFull = (v) => v ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

const ACTIVITY_ICONS = {
  loan_active: { icon: Briefcase, color: '#059669', bg: '#ecfdf5' },
  offer_submitted: { icon: ArrowUpRight, color: '#2563eb', bg: '#eff6ff' },
  draft_created: { icon: FileText, color: '#6b7280', bg: '#f3f4f6' },
  offer_declined: { icon: ArrowDownRight, color: '#dc2626', bg: '#fef2f2' },
};

const FunderPortfolioDashboard = ({ onNavigate, user, availableCount }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile-completeness check — drives the missing-info banner.
  const profileMissing = (() => {
    if (!user) return [];
    const required = [
      { key: 'phone', label: 'Phone' },
      { key: 'address1', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zip', label: 'Zip' },
    ];
    return required.filter(f => {
      const v = user[f.key];
      return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
    }).map(f => f.label);
  })();

  // Fee summary line — shows funder their current setup at-a-glance.
  const brokerPct = user && (user.broker_fee_percentage ?? null);
  const brokerCap = user && user.broker_fee_cap;
  const appFee = user && (user.application_fee ?? null);

  useEffect(() => {
    (async () => {
      try {
        setStats(await apiGet('/funder/dashboard-stats'));
      } catch (e) { console.error('Failed to load dashboard:', e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: 'var(--mcl-text-muted)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid var(--mcl-border)', borderTopColor: 'var(--mcl-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        Loading dashboard...
      </div>
    </div>
  );

  if (!stats) return null;

  const { portfolio, pipeline, recent_activity, loan_summaries } = stats;

  // The count of applications still waiting for an offer from THIS funder,
  // computed upstream from the same list Browse renders. `pipeline.available_apps`
  // is the marketplace-wide approved count and answers a different question —
  // it can read 40 while this funder has nothing left to bid on.
  const actionable = availableCount ?? 0;

  // Bars used to be scaled against available_apps, which was reliably the
  // largest number. The actionable count is not, so the scale has to come from
  // whatever row is actually biggest or the others overflow their track.
  const pipelineScale = Math.max(
    actionable, pipeline.favorites, pipeline.drafts,
    pipeline.submitted, pipeline.accepted, pipeline.declined,
  );

  return (
    <div data-testid="funder-portfolio-dashboard">
      {/* Info bar banners — surfaced at the top of the dashboard */}
      {actionable > 0 && (
        <div
          data-testid="available-applications-banner"
          style={{
            marginBottom: '1rem', padding: '0.85rem 1rem',
            background: '#ecfdf5', border: '1px solid #6ee7b7', borderLeft: '4px solid #059669',
            borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}
        >
          <Search size={18} style={{ color: '#059669', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem', marginBottom: '0.15rem' }}>
              {actionable} application{actionable === 1 ? '' : 's'} {actionable === 1 ? 'is' : 'are'} ready for your offer
            </div>
            <div style={{ fontSize: '0.82rem', color: '#047857', lineHeight: 1.45 }}>
              Approved cases matching your funding settings that you have not bid on yet.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onNavigate && onNavigate('browse')}
            data-testid="goto-browse-btn"
            className="mcl-btn mcl-btn-sm mcl-btn-outline"
            style={{ flexShrink: 0, color: '#065f46', borderColor: '#6ee7b7' }}
          >
            Browse Applications <ChevronRight size={14} />
          </button>
        </div>
      )}

      {profileMissing.length > 0 && (
        <div
          data-testid="profile-incomplete-banner"
          style={{
            marginBottom: '1rem', padding: '0.85rem 1rem',
            background: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '4px solid #d97706',
            borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
          }}
        >
          <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: '#78350f', fontSize: '0.9rem', marginBottom: '0.15rem' }}>
              Complete your profile
            </div>
            <div style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: 1.45 }}>
              Missing: <strong>{profileMissing.join(', ')}</strong>. Funded clients and law firms see your contact info on every offer — please finish your profile so payments and follow-ups can reach you.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onNavigate && onNavigate('profile')}
            data-testid="goto-profile-btn"
            className="mcl-btn mcl-btn-sm mcl-btn-outline"
            style={{ flexShrink: 0, color: '#92400e', borderColor: '#fcd34d' }}
          >
            Complete Profile <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div
        data-testid="funder-fees-banner"
        style={{
          marginBottom: '1.5rem', padding: '0.85rem 1rem',
          background: '#eff6ff', border: '1px solid #bfdbfe', borderLeft: '4px solid #2563eb',
          borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap',
        }}
      >
        <Info size={18} style={{ color: '#2563eb', flexShrink: 0 }} />
        <div style={{ flex: '1 1 auto', minWidth: 0, fontSize: '0.85rem', color: '#1e3a8a' }}>
          <strong style={{ marginRight: '0.4rem' }}>Your current fees:</strong>
          <span data-testid="funder-broker-fee-display">
            Broker fee <strong>{brokerPct === null || brokerPct === undefined ? '—' : `${brokerPct}%`}</strong>
            {brokerCap ? <> (cap ${parseFloat(brokerCap).toLocaleString()})</> : null}
          </span>
          <span style={{ margin: '0 0.5rem', color: '#93c5fd' }}>|</span>
          <span data-testid="funder-application-fee-display">
            Application fee <strong>{appFee === null || appFee === undefined ? '$99 (default)' : `$${parseFloat(appFee).toLocaleString()}`}</strong>
          </span>
        </div>
        <button
          type="button"
          onClick={() => onNavigate && onNavigate('settings')}
          data-testid="goto-fees-btn"
          className="mcl-btn mcl-btn-sm mcl-btn-outline"
          style={{ flexShrink: 0, color: '#1e3a8a', borderColor: '#bfdbfe' }}
        >
          Change Fees <ChevronRight size={14} />
        </button>
      </div>

      {/* KPI Cards Row */}
      <div data-testid="kpi-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <KPICard
          label="Capital Deployed"
          value={`$${fmt(portfolio.total_deployed)}`}
          sublabel={`${portfolio.active_loans} active loan${portfolio.active_loans !== 1 ? 's' : ''}`}
          icon={DollarSign}
          accent="#059669"
          accentBg="#ecfdf5"
        />
        <KPICard
          label="Total Obligation"
          value={`$${fmt(portfolio.total_obligation)}`}
          sublabel="Financed amount"
          icon={TrendingUp}
          accent="#2563eb"
          accentBg="#eff6ff"
        />
        <KPICard
          label="Max Cap Exposure"
          value={`$${fmt(portfolio.total_max_cap)}`}
          sublabel="Maximum liability"
          icon={Target}
          accent="#7c3aed"
          accentBg="#f5f3ff"
        />
        <KPICard
          label="Avg. Rate"
          value={`${portfolio.avg_rate}%`}
          sublabel="Annualized"
          icon={Activity}
          accent="#ea580c"
          accentBg="#fff7ed"
        />
        <KPICard
          label="Pipeline Value"
          value={`$${fmt(pipeline.pipeline_value)}`}
          sublabel={`${pipeline.drafts} drafts, ${pipeline.submitted} submitted`}
          icon={FileText}
          accent="#0891b2"
          accentBg="#ecfeff"
        />
      </div>

      {/* Pipeline + Activity Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
        {/* Pipeline Funnel */}
        <div className="mcl-card" data-testid="pipeline-funnel">
          <div className="mcl-card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="mcl-card-header-title" style={{ fontSize: '0.9375rem' }}>Deal Pipeline</h3>
          </div>
          <div className="mcl-card-body" style={{ padding: '0 1.25rem 1.25rem' }}>
            <PipelineBar label="Available" count={actionable} total={pipelineScale} color="#94a3b8" onClick={() => onNavigate('browse')} />
            <PipelineBar label="Favorites" count={pipeline.favorites} total={pipelineScale} color="#f59e0b" />
            <PipelineBar label="Drafts" count={pipeline.drafts} total={pipelineScale} color="#6b7280" />
            <PipelineBar label="Submitted" count={pipeline.submitted} total={pipelineScale} color="#3b82f6" />
            <PipelineBar label="Accepted" count={pipeline.accepted} total={pipelineScale} color="#059669" />
            {pipeline.declined > 0 && <PipelineBar label="Declined" count={pipeline.declined} total={pipelineScale} color="#ef4444" />}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mcl-card" data-testid="recent-activity">
          <div className="mcl-card-header" style={{ paddingBottom: '0.75rem' }}>
            <h3 className="mcl-card-header-title" style={{ fontSize: '0.9375rem' }}>Recent Activity</h3>
          </div>
          <div className="mcl-card-body" style={{ padding: '0 1.25rem 1.25rem' }}>
            {recent_activity.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--mcl-text-muted)', fontSize: '0.875rem' }}>No recent activity</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {recent_activity.slice(0, 6).map((a, i) => {
                  const config = ACTIVITY_ICONS[a.type] || ACTIVITY_ICONS.draft_created;
                  const Icon = config.icon;
                  return (
                    <div key={i} data-testid={`activity-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0', borderBottom: i < Math.min(recent_activity.length, 6) - 1 ? '1px solid var(--mcl-border)' : 'none' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: config.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={15} color={config.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--mcl-text-heading)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.text}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)' }}>
                          {formatInAppZone(a.date, { month: 'short', day: 'numeric' })}
                          {a.case_type && ` · ${a.case_type}`}
                        </div>
                      </div>
                      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: config.color, flexShrink: 0 }}>${fmt(a.amount)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Loans Quick View */}
      <div className="mcl-card" data-testid="loans-quick-view">
        <div className="mcl-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="mcl-card-header-title" style={{ fontSize: '0.9375rem' }}>Active Loans</h3>
          {loan_summaries.length > 0 && (
            <button onClick={() => onNavigate('my-loans')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mcl-primary)', fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }} data-testid="view-all-loans">
              View All <ChevronRight size={14} />
            </button>
          )}
        </div>
        <div className="mcl-card-body-flush">
          {loan_summaries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--mcl-text-muted)', fontSize: '0.875rem' }}>
              No active loans yet. Browse applications and submit offers to grow your portfolio.
            </div>
          ) : (
            <table className="mcl-table" data-testid="loans-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Case Type</th>
                  <th style={{ textAlign: 'right' }}>Advance</th>
                  <th style={{ textAlign: 'right' }}>Obligation</th>
                  <th style={{ textAlign: 'right' }}>Max Cap</th>
                  <th style={{ textAlign: 'center' }}>Rate</th>
                  <th style={{ textAlign: 'center' }}>Accepted</th>
                </tr>
              </thead>
              <tbody>
                {loan_summaries.map((loan, i) => (
                  <tr key={i} data-testid={`loan-row-${i}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.875rem' }}>{loan.client_name}</div>
                      {loan.state && <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)' }}>{loan.state}</div>}
                    </td>
                    <td style={{ fontSize: '0.8125rem' }}>{loan.case_type}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#065f46' }}>${fmtFull(loan.advance_amount)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>${fmtFull(loan.total_funded_amount)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>${fmtFull(loan.maximum_cap)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{loan.annualized_rate}%</td>
                    <td style={{ textAlign: 'center', fontSize: '0.8125rem' }}>{loan.accepted_at ? formatDate(loan.accepted_at) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* KPI Card */
const KPICard = ({ label, value, sublabel, icon: Icon, accent, accentBg }) => (
  <div className="mcl-card" style={{ overflow: 'hidden' }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div style={{ padding: '1.125rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>{label}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--mcl-text-heading)', lineHeight: 1.1 }}>{value}</div>
          {sublabel && <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', marginTop: '4px' }}>{sublabel}</div>}
        </div>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} color={accent} />
        </div>
      </div>
    </div>
    <div style={{ height: '3px', background: accent }} />
  </div>
);

/* Pipeline Bar */
const PipelineBar = ({ label, count, total, color, onClick }) => {
  const pct = total > 0 ? Math.max((count / total) * 100, count > 0 ? 8 : 0) : 0;
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      data-testid={`pipeline-${label.toLowerCase()}`}
    >
      <div style={{ width: '80px', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--mcl-text-heading)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: '20px', background: '#f1f5f9', borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
        <div data-testid="pipeline-bar-fill" style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '6px', transition: 'width 0.6s ease', minWidth: count > 0 ? '20px' : '0' }} />
      </div>
      <div data-testid="pipeline-bar-count" style={{ width: '32px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color }}>{count}</div>
    </div>
  );
};

export default FunderPortfolioDashboard;
